import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as S from "./style";
import { 
  ArrowLeft, 
  User, 
  CreditCard, 
  Calendar, 
  Hash, 
  Info, 
  ArrowUpRight, 
  ArrowDownLeft,
  FileText
} from "lucide-react";
import { Transaction } from "@apis/index";
import { Helper } from "@utils/index";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import styled from "@emotion/styled";

const TransactionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { settings: currencySettings } = useGeneralSettings();

  useEffect(() => {
    const fetchDetails = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const res: any = await Transaction.getTransactionById(Number(id));
        if (res) {
          setData(res);
        }
      } catch (error) {
        console.error("Failed to fetch transaction details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [id]);

  if (loading) {
    return (
      <S.Container>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
          Loading transaction details...
        </div>
      </S.Container>
    );
  }

  if (!data) {
    return (
      <S.Container>
        <div style={{ textAlign: "center", marginTop: "50px" }}>
          <h3>Transaction not found</h3>
          <button 
            onClick={() => navigate(-1)} 
            style={{ 
              marginTop: "16px", 
              padding: "8px 16px", 
              background: "#6366f1", 
              color: "#fff", 
              border: "none", 
              borderRadius: "6px",
              cursor: "pointer"
            }}
          >
            Go Back
          </button>
        </div>
      </S.Container>
    );
  }

  return (
    <S.Container>
      <div style={{ marginBottom: "24px" }}>
        <button 
          onClick={() => navigate(-1)}
          style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "8px", 
            background: "none", 
            border: "none", 
            cursor: "pointer", 
            color: "#64748b",
            fontSize: "14px",
            fontWeight: 500,
            padding: 0
          }}
        >
          <ArrowLeft size={18} /> Back to Reports
        </button>
      </div>

      <S.Header>
        <S.Title>Transaction Detail {`#${data.id}`}</S.Title>
        <S.SubTitle>Complete overview of this financial movement.</S.SubTitle>
      </S.Header>

      <GridWrapper>
        {/* Left Column: Transaction Summary */}
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          <DetailCard>
            <CardHeader>
              <CreditCard size={20} />
              <h4>Transaction Summary</h4>
            </CardHeader>
            <CardBody>
              <InfoRow>
                <InfoLabel>ID</InfoLabel>
                <InfoValue>#{data.id}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Date & Time</InfoLabel>
                <InfoValue>{new Date(data.createdAt).toLocaleString()}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Status</InfoLabel>
                <InfoValue>
                  <S.StatusPill 
                    bg={data.status === 'Success' ? '#f6ffed' : '#fff1f0'}
                    color={data.status === 'Success' ? '#52c41a' : '#ff4d4f'}
                  >
                    {data.status}
                  </S.StatusPill>
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Purpose</InfoLabel>
                <InfoValue style={{ textTransform: "capitalize" }}>{data.purpose?.replaceAll("_", " ")}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Method</InfoLabel>
                <InfoValue style={{ textTransform: "uppercase" }}>{data.method?.replaceAll("_", " ")}</InfoValue>
              </InfoRow>
            </CardBody>
          </DetailCard>

          <DetailCard>
            <CardHeader>
              <User size={20} />
              <h4>User Information</h4>
            </CardHeader>
            <CardBody>
              <InfoRow>
                <InfoLabel>Name</InfoLabel>
                <InfoValue>{data.user?.name || "N/A"}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Role</InfoLabel>
                <InfoValue style={{ textTransform: "uppercase" }}>{data.user?.role || "N/A"}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>{data.user?.email || "N/A"}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Phone Number</InfoLabel>
                <InfoValue>{data.user?.phoneNo || "N/A"}</InfoValue>
              </InfoRow>
            </CardBody>
          </DetailCard>
        </div>

        {/* Right Column: Financial Data */}
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          <DetailCard>
            <CardHeader>
              <FileText size={20} />
              <h4>Financial Details</h4>
            </CardHeader>
            <CardBody>
              <AmountSection type={data.type}>
                <div style={{ fontSize: "16px", color: "#64748b", marginBottom: "8px" }}>
                  {data.type === 'credit' ? 'Credit Amount' : 'Debit Amount'}
                </div>
                <div style={{ fontSize: "32px", fontWeight: 800 }}>
                  {data.type === 'credit' ? <ArrowDownLeft size={28} /> : <ArrowUpRight size={28} />}
                  {Helper.formatCurrency(data.amount, currencySettings)}
                </div>
              </AmountSection>

              <div style={{ marginTop: "24px" }}>
                <InfoRow>
                  <InfoLabel>Closing Balance</InfoLabel>
                  <InfoValue style={{ fontSize: "18px" }}>{Helper.formatCurrency(data.closingBalance, currencySettings)}</InfoValue>
                </InfoRow>
                <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
                  This balance reflects the state of the account immediately after this transaction.
                </div>
              </div>
            </CardBody>
          </DetailCard>

          {data.orderId && (
            <DetailCard>
              <CardHeader>
                <Hash size={20} />
                <h4>Related Order</h4>
              </CardHeader>
              <CardBody>
                <InfoRow>
                  <InfoLabel>Order ID</InfoLabel>
                  <InfoValue>#{data.orderId}</InfoValue>
                </InfoRow>
                <div style={{ marginTop: "16px" }}>
                  <button 
                    onClick={() => navigate(`/admin/orders/details/${data.orderId}`)}
                    style={{ 
                      width: "100%",
                      padding: "10px", 
                      background: "#f8fafc", 
                      border: "1px solid #e2e8f0", 
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#1e293b",
                      transition: "all 0.2s"
                    }}
                    onMouseOver={(e: any) => { e.target.style.background = "#eff6ff"; e.target.style.borderColor = "#bfdbfe"; }}
                    onMouseOut={(e: any) => { e.target.style.background = "#f8fafc"; e.target.style.borderColor = "#e2e8f0"; }}
                  >
                    View Order Details
                  </button>
                </div>
              </CardBody>
            </DetailCard>
          )}

          {data.refId && data.refId !== "manual_settlement" && (
            <DetailCard>
              <CardHeader>
                <Info size={20} />
                <h4>Reference Info</h4>
              </CardHeader>
              <CardBody>
                <InfoRow>
                  <InfoLabel>Reference ID</InfoLabel>
                  <InfoValue>{data.refId}</InfoValue>
                </InfoRow>
              </CardBody>
            </DetailCard>
          )}
        </div>
      </GridWrapper>
    </S.Container>
  );
};

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const DetailCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 16px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #1e293b;

  h4 {
    font-size: 15px;
    font-weight: 700;
    margin: 0;
  }
`;

const CardBody = styled.div`
  padding: 24px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px dashed #f1f5f9;
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  &:first-child {
    padding-top: 0;
  }
`;

const InfoLabel = styled.div`
  font-size: 14px;
  color: #64748b;
`;

const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
`;

const AmountSection = styled.div<{ type?: string }>`
  background: ${props => props.type === 'credit' ? '#f0fdf4' : '#fef2f2'};
  padding: 24px;
  border-radius: 12px;
  text-align: center;
  color: ${props => props.type === 'credit' ? '#166534' : '#991b1b'};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export default TransactionDetails;
